import { useMutation, UseMutationOptions, useQuery, UseQueryOptions } from '@tanstack/react-query'
import { AxiosError } from 'axios'
import { useMemo } from 'react'
import { useHistory } from 'react-router-dom'
import { toast } from 'react-toastify'
import i18n from '../../i18n'
import { mapValuesToSelectOptions } from '../../utils/formUtils/forms'
import { DeleteMultipleResponse, PageListParams } from '../globalTypes'
import { invalidateQueries, updateToast } from '../helpers'
import { createUpdateProject, deleteProjectById, deleteProjects, fetchProjectValues, getProjectById, getProjects } from './functions'
import {
    Project,
    ProjectCriteria,
    ProjectDeleteParams,
    ProjectMutationResponse,
    ProjectParams,
    ProjectPayload,
    ProjectsDeleteParams,
    ProjectValuesData,
} from './types'

const projects = {
    GetProject: {
        useQuery: (id: number, params?: ProjectParams, options?: Omit<UseQueryOptions<Project, AxiosError>, 'queryKey' | 'queryFn'>) =>
            useQuery({
                queryKey: params ? ['projects', id, params] : ['projects', id],
                queryFn: () => getProjectById(id, params),
                staleTime: 60 * 1000,
                ...options,
            }),
    },
    GetProjects: {
        useQuery: (params: PageListParams<ProjectCriteria>, options?: Omit<UseQueryOptions<Project[], AxiosError>, 'queryKey' | 'queryFn'>) => {
            return useQuery({
                queryKey: ['projects', 'list', params],
                queryFn: () => getProjects(params),
                staleTime: 60 * 1000,
                ...options,
                enabled: options?.enabled ?? true,
            })
        },
    },
    CreateUpdateProject: {
        useMutation: (options?: UseMutationOptions<ProjectMutationResponse, AxiosError, ProjectPayload>) => {
            const history = useHistory()

            return useMutation({
                onMutate: () => {
                    const toastId = toast.loading(i18n.t('toast.loading') as string)
                    return toastId
                },
                mutationFn: (payload: ProjectPayload) => createUpdateProject(payload),
                onSuccess: (data, variables, context) => {
                    const { isEdit } = variables

                    const toastId = context as number
                    toastId && updateToast(toastId, i18n.t('toast.update.success') as string, 'success')

                    invalidateQueries(['projects', data.id.toString()])
                    invalidateQueries(['projects', 'list'])

                    !isEdit && history.push(`/projects/${data.id}/edit`)
                },
                onError: (error, variables, context) => {
                    const toastId = context as number
                    toastId && updateToast(toastId, i18n.t('toast.update.error') as string, 'error')
                },
                ...options,
            })
        },
    },
    DeleteProject: {
        useMutation: (options?: UseMutationOptions<ProjectMutationResponse, AxiosError, ProjectDeleteParams>) => {
            return useMutation({
                onMutate: (params: ProjectDeleteParams) => {
                    toast.loading(i18n.t('toast.loading.delete') as string, { toastId: params.id })
                },
                mutationFn: (params: ProjectDeleteParams) => deleteProjectById(params.id),
                onSuccess: (data, params) => {
                    const { id } = params ?? {}
                    updateToast(id, i18n.t('toast.update.delete.success') as string, 'success')

                    invalidateQueries(['projects', 'list'])
                },
                onError: (error, params) => {
                    updateToast(params.id, i18n.t('toast.update.delete.error') as string, 'error')
                    console.log('Error deleting project:', error)
                },
                ...options,
            })
        },
    },
    DeleteProjects: {
        useMutation: (options?: UseMutationOptions<DeleteMultipleResponse, AxiosError, ProjectsDeleteParams>) => {
            return useMutation({
                onMutate: (params: ProjectsDeleteParams) => {
                    const { ids } = params
                    toast.loading(i18n.t('toast.loading.delete.project.number', { requestsToBeDeletedCount: ids.length }) as string, {
                        toastId: ids[0],
                    })
                },
                mutationFn: (params: ProjectsDeleteParams) => deleteProjects(params.ids),
                onSuccess: (data, params) => {
                    const { ids } = params ?? {}

                    const failedCount = data?.notFoundIds?.length
                    const successCount = data?.successfulDelete?.length
                    const requestsToBeDeletedCount = ids?.length

                    const successMessage = i18n.t('toast.loading.delete.project.number.success', { successCount, requestsToBeDeletedCount }) as string
                    const partialErrorMessage = i18n.t('toast.loading.delete.project.number.error', {
                        failedCount,
                        requestsToBeDeletedCount,
                    }) as string

                    if (failedCount > 0) {
                        updateToast(ids?.[0], partialErrorMessage, 'error')
                    }

                    if (successCount > 0) {
                        updateToast(ids?.[0], successMessage, 'success')
                        invalidateQueries(['projects', 'list'])
                    }
                },
                onError: (error, params) => {
                    const { ids } = params ?? {}
                    updateToast(ids?.[0], i18n.t('toast.update.delete.error'), 'error')
                    console.error(error)
                },
                ...options,
            })
        },
    },
    FormValues: {
        useQuery: (fields?: string[], options?: Omit<UseQueryOptions<ProjectValuesData, AxiosError>, 'queryKey' | 'queryFn'>) => {
            const query = useQuery({
                queryKey: ['projectValues', fields],
                queryFn: () => fetchProjectValues(fields),
                staleTime: Infinity,
                gcTime: Infinity,
                ...options,
            })

            const formattedData = useMemo(() => {
                return mapValuesToSelectOptions(query.data)
            }, [query.data])

            return {
                ...query,
                data: formattedData,
            }
        },
    },
}

export default projects
